import {ValueFormatter} from '@epic-visualizations/charts';

import {AveragePlaytimeDataItem} from '../reducers/fortnite-game/averagePlayTime.reducer';
import {ActiveUsersDataItem} from '../reducers/fortnite-game/activeUsers.reducer';
import {LifetimePlaysDataItem} from '../reducers/fortnite-game/lifetimePlays.reducer';
import {TimeScale, DateMoment} from './Date';
import {DefaultData} from '../reducers/fortnite-game/types';

export type Formatter<T extends Date | number | string> = (
    locale?: string | number
) => // @ts-ignore:next-line
ValueFormatter<T>;

export interface Option {
    label: string;
    secondaryText?: string;
    groupName?: string;
    value: string | number;
    icon?: React.ReactElement;
    order?: number;
}

export enum CombinedPlatform {
    CLIENT_OVERALL = 'client_overall',
    CLIENT_CONSOLE = 'client_console',
    CLIENT_CONSOLE_NG = 'client_console_ng',
    CLIENT_CONSOLE_MOBILE = 'client_console_mobile',
    CLIENT_PC = 'client_pc',
    CLIENT_MOBILE = 'client_mobile'
}

export enum Platform {
    PLAYSTATION_4 = 'PS4',
    PLAYSTATION_5 = 'PS5',
    XBOX_ONE = 'XboxOne',
    XBOX_SERIES_X = 'XSX',
    NINTENDO_SWITCH = 'Switch',
    WINDOWS = 'Windows',
    ANDROID = 'Android'
}

export enum DeviceStat {
    EXPECTED_FRAMES = 'ef',
    FRAME_COUNT = 'fc',
    TIME_MEASURED_SEC = 'sec',
    TOTAL_HITCHES = 'th'
}

export interface DeviceDataItem {
    [DeviceStat.EXPECTED_FRAMES]: number;
    [DeviceStat.FRAME_COUNT]: number;
    [DeviceStat.TIME_MEASURED_SEC]: number;
    [DeviceStat.TOTAL_HITCHES]: number;
}

export interface PlatformData {
    [Platform.PLAYSTATION_4]: DeviceDataItem;
    [Platform.PLAYSTATION_5]: DeviceDataItem;
    [Platform.XBOX_ONE]: DeviceDataItem;
    [Platform.XBOX_SERIES_X]: DeviceDataItem;
    [Platform.NINTENDO_SWITCH]: DeviceDataItem;
    [Platform.WINDOWS]: DeviceDataItem;
    [Platform.ANDROID]: DeviceDataItem;
}

export type WidgetDataItem = ActiveUsersDataItem | AveragePlaytimeDataItem | LifetimePlaysDataItem;

export interface WidgetData {
    [TimeScale.LIVE]: DefaultData<WidgetDataItem>;
    [TimeScale.DAILY]: DefaultData<WidgetDataItem>;
    [TimeScale.WEEKLY]: DefaultData<WidgetDataItem>;
    [TimeScale.MONTHLY]: DefaultData<WidgetDataItem>;
}

export interface WidgetState {
    data: WidgetData;
    loading: boolean;
    timeScale?: keyof WidgetData;
    lastFetch?: number;
    error?: string;
    reload: (silent: boolean, overwriteCache: boolean) => void;
}

export enum MyOverallHighlight {
    UNIQUE_PLAYERS = 'uniquePlayers',
    MONTHLY = 'monthly',
    WEEKLY = 'weekly',
    LIFETIME_PLAYS = 'lifetimePlays'
}

export enum MyTopPerformingGame {
    MOST_POPULAR_GAME = 'mostPopularGame',
    HIGHEST_GROWTH = 'highestGrowth',
    HIGHEST_AVG_PLAY_TIME = 'highestAvgPlayTime',
    MOST_VISITED_GAME = 'mostVisitedGame'
}

export enum MyTop10Game {
    ACTIVE_PLAYERS = 'activePlayers',
    AVERAGE_PLAY_TIME = 'averagePlayTime',
    PLAYER_RETENTION = 'playerRetention',
    TOTAL_LIFE_TIME_PLAYS = 'totalLifetimePlays'
}

export interface GameDataItem {
    value: number | string;
    creativeIslandCode: string;
    label?: string;
    trend?: number;
}

export interface Version {
    created: string;
    updated: string;
    rvn: number;
    mnemonic: string;
    version: number;
    activated: string;
    id: string;
    displaced?: string;
}

export interface Releases {
    [key: string]: Pick<Version, 'version' | 'activated' | 'displaced'>[];
}

export enum Payout {
    ENGAGEMENT = 'engagement',
    SAC = 'sac'
}

export enum Playrate {
    CURRENT = 'current',
    PREVIOUS = 'previous'
}

export enum XpCalibrationStatus {
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
    DATA_ERROR = 'no_data'
}

export interface ActivationData {
    startDate: DateMoment;
    endDate: DateMoment;
    name: string;
    description: string;
    activations?: number;
    previousActivations?: number;
}
